import { useEffect, useRef, useState } from 'react';
import { HealthIcon, RefreshIcon } from './Icons';
import { getHealth } from '../services/healthService';
import './HealthStatusButton.css';

function HealthStatusButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const rootRef = useRef(null);

  const loadHealth = async () => {
    setLoading(true);

    try {
      const data = await getHealth();
      setResult(data);
    } catch (err) {
      setResult({
        httpStatus: 0,
        ok: false,
        body: {
          success: false,
          data: {
            status: 'error',
            backend: 'down',
            db: 'unknown',
            message: err.message || 'Failed to load health status',
          },
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = async () => {
    const nextOpen = !open;
    setOpen(nextOpen);

    if (nextOpen) {
      await loadHealth();
    }
  };

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function handleClickOutside(event) {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const statusTone = result
    ? result.ok
      ? 'ok'
      : result.body?.data?.backend === 'down'
        ? 'down'
        : 'warn'
    : 'idle';

  const jsonText = result
    ? JSON.stringify(result.body, null, 2)
    : loading
      ? 'Loading…'
      : 'No response yet.';

  const statusLabel =
    result?.httpStatus > 0
      ? `HTTP ${result.httpStatus}`
      : result && result.httpStatus === 0
        ? 'Unreachable'
        : '';

  return (
    <div className="health-status" ref={rootRef}>
      <button
        type="button"
        className={`health-status__trigger health-status__trigger--${statusTone}${
          open ? ' health-status__trigger--open' : ''
        }`}
        onClick={handleOpen}
        aria-label="Server health"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <HealthIcon />
      </button>

      {open && (
        <div className="health-status__popup" role="dialog" aria-label="Health response">
          <div className="health-status__popup-header">
            <div>
              <strong>Health</strong>
              {statusLabel ? <span>{statusLabel}</span> : null}
            </div>
            <button
              type="button"
              className="health-status__refresh"
              onClick={loadHealth}
              disabled={loading}
              aria-label="Refresh health"
            >
              <RefreshIcon />
            </button>
          </div>
          <pre
            className={`health-status__json${
              result && !result.ok ? ' health-status__json--error' : ''
            }`}
          >
            {jsonText}
          </pre>
        </div>
      )}
    </div>
  );
}

export default HealthStatusButton;
